import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  signOut,
  type Auth,
  type ConfirmationResult,
} from 'firebase/auth'
import { getFunctions, type Functions } from 'firebase/functions'
import { type AppCheck } from 'firebase/app-check'
import { firebaseConfig, initAppCheckFor } from './firebase'
import { OPS_CALLABLE_REGION } from './functionRegions.generated'
import { sendOutsidePhoneOtp, clearOutsidePhoneVerifier } from './outsidePhoneOtp'

export const APPROVALS_APP_NAME = 'outside-approvals'

export type { ConfirmationResult }

let approvalsApp: FirebaseApp | undefined
let approvalsAppCheck: AppCheck | undefined
let approvalsAppCheckInitialized = false
let approvalsAuth: Auth | undefined
let approvalsFunctions: Functions | undefined

export function getApprovalsApp(): FirebaseApp {
  if (!approvalsApp) {
    const existing = getApps().find((a) => a.name === APPROVALS_APP_NAME)
    approvalsApp = existing ?? initializeApp(firebaseConfig, APPROVALS_APP_NAME)
  }
  return approvalsApp
}

export function getApprovalsAppCheck(): AppCheck | undefined {
  if (!approvalsAppCheckInitialized) {
    approvalsAppCheckInitialized = true
    approvalsAppCheck = initAppCheckFor(getApprovalsApp())
  }
  return approvalsAppCheck
}

export function getApprovalsAuth(): Auth {
  getApprovalsAppCheck()
  if (!approvalsAuth) {
    const app = getApprovalsApp()
    try {
      approvalsAuth = initializeAuth(app, {
        persistence: inMemoryPersistence,
      })
    } catch {
      approvalsAuth = getAuth(app)
    }
  }
  return approvalsAuth
}

export function getApprovalsFunctions(): Functions {
  getApprovalsAppCheck()
  if (!approvalsFunctions) {
    approvalsFunctions = getFunctions(getApprovalsApp(), OPS_CALLABLE_REGION)
  }
  return approvalsFunctions
}

export function resetApprovalsAppForTesting(): void {
  clearOutsidePhoneVerifier()
  approvalsApp = undefined
  approvalsAppCheck = undefined
  approvalsAppCheckInitialized = false
  approvalsAuth = undefined
  approvalsFunctions = undefined
}

export async function sendApprovalsPhoneOtp(
  phoneNumber: string,
  container?: HTMLElement | string,
): Promise<ConfirmationResult> {
  const auth = getApprovalsAuth()
  return sendOutsidePhoneOtp(auth, phoneNumber, container, 'approvals-recaptcha-container')
}

export async function signOutApprovals(): Promise<void> {
  clearOutsidePhoneVerifier()
  try {
    const auth = getApprovalsAuth()
    await signOut(auth)
  } catch {
    // ignore
  }
}
